import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth';
import {
  messageService,
  GetMessagesRequest,
  SendMessageRequest,
  BroadcastMessageRequest,
} from '../services/message.service';

/**
 * Query key factory for message queries
 */
export const messageKeys = {
  all: ['messages'] as const,
  list: (params: GetMessagesRequest) => [...messageKeys.all, 'list', params] as const,
  unreadCount: () => [...messageKeys.all, 'unread'] as const,
};

/**
 * Hook to fetch messages
 */
export const useMessages = (params: GetMessagesRequest) => {
  return useQuery({
    queryKey: messageKeys.list(params),
    queryFn: () => messageService.getMessages(params),
  });
};

/**
 * Hook to fetch unread message count for the current user
 * Matches Angular: calls /count with MessageFor=userId, Read=false
 */
export const useUnreadCount = () => {
  const { user } = useAuth();
  const userId = user?.profile?.sub;

  return useQuery({
    queryKey: [...messageKeys.unreadCount(), userId],
    queryFn: () => messageService.getUnreadCount(userId!),
    enabled: !!userId,
    refetchInterval: 60000, // Refresh every minute
  });
};

/**
 * Hook to send a message
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SendMessageRequest) => messageService.sendMessage(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
};

/**
 * Hook to broadcast a message
 */
export const useBroadcastMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: BroadcastMessageRequest) => messageService.broadcastMessage(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
};

/**
 * Hook to mark messages as read
 */
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageIds: string[]) => messageService.markAsRead(messageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
};

/**
 * Hook to delete a message
 */
export const useDeleteMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => messageService.deleteMessage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: messageKeys.all });
    },
  });
};
